import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';
import queryString from 'query-string';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { hasRoleAdmin } from 'common/userRoles';
import { PARTNER_BILLING_PLAN_LITE } from 'common/partnerConstants';

import {
  LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS_TO_VALUE,
} from 'common/leadConstants';

import SrFrontConversation from 'client/components/SrFrontConversation';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrStarRating from 'client/components/SrStarRating';

const getSupraShowingId = (location) => {
  const locationQuery = queryString.parse(location.search) || {};
  return locationQuery.supraShowingId ? parseInt(locationQuery.supraShowingReviewId, 10) : null;
};

const getTourId = (location) => {
  const locationQuery = queryString.parse(location.search) || {};
  return locationQuery.tourFeedbackId ? parseInt(locationQuery.tourFeedbackId, 10) : null;
};

const SrPartnerListingUserTourReview = ({
  className,
  startDate,
  review,
  apartmentUnit,
  user,
  tourListing,
  showTourLink,
  frontConversation,
  tourFeedback,
  tourFeedbackByUserName,
  agentName,
  dispatch,
}) => {
  const existingTourGuideComments = !!(
    tourListing && (
      (tourListing.agentComment || '').trim() || (tourListing.negativeRenterComment || '').trim()
    )
  );
  const existingFeedback = !!(review && review.feedback);
  const pillsDOM = review?.tags.length > 0 && (
    <span className={classnames('comment', { notReviewed: !review })}>
      {review.rating < 5 ? 'What could improve: ' : 'What they liked: '}
      {`"${review.tags.join(', ')}"`}
    </span>
  );

  return (
    <div className='showing'>
      <div className={classnames('header', className)}>
        <div>
          <span className='rating'>
            <SrStarRating
              rate={review ? review.rating : 5}
              disabled={!review}
              narrow
            />
            <span className='rate'>
              {review
                ? `${pluralize('star', review.rating, true)}`
                : (
                  <SrText
                    opacity='0.33'
                    font='greycliff'
                    color='yellowGold'
                    weight='800'
                  >
                    N/A
                  </SrText>
                )
              }
            </span>
            {apartmentUnit && (
              <span className='unit'>Unit {apartmentUnit}</span>
            )}
            {tourFeedbackByUserName && (
              <span className='unit'>{tourFeedbackByUserName}</span>
            )}
            {agentName && (
              <span className='unit'>{agentName}</span>
            )}
            {user && (
              <span className='unit'>{user.name}</span>
            )}
            {tourListing && (
              <span
                className={
                  classnames('unit', tourListing.archiveReasons === null && 'statusActive')
                }
              >
                {tourListing.archiveReasons ? 'Archived' : 'Active'} Lead
              </span>
            )}
            {user && (
              <span className='unit'>{user.phone}</span>
            )}
            {showTourLink && (
              <span className='unit blue-link'>
                <SrLink color='blue300' to={`/admin/tours/all/user/${user.id}`}>
                  Tour
                </SrLink>
              </span>
            )}
            {frontConversation && (
              <div className='unit'>
                <SrFrontConversation
                  frontConversation={frontConversation}
                  user={user}
                  dispatch={dispatch}
                  ctaClassName='blue-link'
                />
              </div>
            )}
          </span>
        </div>
        <span className='startDate'>
          {momentWithAustinTimezone(startDate).format('MMM D')}
        </span>
      </div>
      {(existingTourGuideComments || existingFeedback || pillsDOM || tourFeedback) && (
        <div className='comments'>
          {!review && tourListing && (
            <span className={classnames('comment notReviewed')}>
              Renter: no review yet
            </span>
          )}
          {existingFeedback && (
            <span className={classnames('comment', { notReviewed: !review })}>
              Renter: {`"${review.feedback}"`}
            </span>
          )}
          {pillsDOM}
          {existingTourGuideComments && (
            <div>
              {tourListing.agentComment && (
                <span className='comment'>
                  Tour Guide (positive): "{tourListing.agentComment}"
                </span>
              )}
              {tourListing.negativeRenterComment && (
                <span className='comment'>
                  Tour Guide (negative): "{tourListing.negativeRenterComment}"
                </span>
              )}
            </div>
          )}
          {tourFeedback && (
            <span className='comment'>
              {tourFeedback}
            </span>
          )}
        </div>
      )}
      {tourListing?.archiveReasons && (
        <div className='archivedSection'>
          <span className='title'>
            {'Archived reason: '}
          </span>
          <ul>
            {tourListing.archiveReasons.map((archiveReason) => {
              const reason = LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS_TO_VALUE[archiveReason] || {};
              const reasonDescription = (
                reason.question
                && tourListing.archiveReasonsDescription[archiveReason]
              );

              return (
                <li key={archiveReason}>
                  <span className='label'>
                    {reason.label}
                    {reasonDescription ? ', ' : ''}
                  </span>
                  {reasonDescription && (
                    <span className='question'>
                      {reason.question} "{reasonDescription}".
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const SrPartnerListingTourReviews = ({
  tourReviews,
  currentUser,
  partner,
  dispatch,
  location,
}) => {
  const isAdmin = hasRoleAdmin(currentUser.roles);
  const showRenterInfo = (
    isAdmin
    || partner.billingPlan === PARTNER_BILLING_PLAN_LITE
  );

  useEffect(() => {
    if (location) {
      const supraShowingId = getSupraShowingId(location);
      if (supraShowingId) {
        const element = global.document.querySelector(`.supraShowingReviewId-${supraShowingId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }

      const tourId = getTourId(location);
      if (tourId) {
        const element = global.document.querySelector(`.tourFeedbackId-${tourId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [location]);

  return (
    <div className='SrDataTablePartnerListingTourReviews'>
      {tourReviews.map((userTours) => {
        const {
          tours,
        } = userTours;

        return (
          <div className='tour'>
            {tours.map((tour, tourIndex) => {
              const props = {
                key: tour.id,
                className: tour.tourListing
                  ? `tourFeedbackId-${tour.id}`
                  : `supraShowingReviewId-${tour.id}`,
                review: tour.review,
                startDate: tour.startDate,
                dispatch: dispatch,
                apartmentUnit: (
                  tour.listing?.apartmentBuildingId && tour.listing?.apartmentUnit
                ) || (
                  tour.apartmentBuildingId && tour.listingApartmentUnit
                ),
                tourFeedback: tour.tourFeedback,
              };

              if (tourIndex === 0) {
                props.tourListing = tour.tourListing;
                props.user = showRenterInfo && tour.user;
                props.showTourLink = isAdmin && !!tour.user;
                props.frontConversation = isAdmin && tour.frontConversation;
                props.tourFeedbackByUserName = showRenterInfo && tour.tourFeedbackByUserName;
                props.agentName = showRenterInfo && tour.agentName;
              }

              return <SrPartnerListingUserTourReview {...props} />;
            })}
          </div>
        );
      })}
      {tourReviews && tourReviews.length === 0 && (
        <div className='tour'>
          <div className='header'>
            <div>
              <span className='rating'>
                <SrStarRating rate={0} disabled narrow />
                <span className='rate'>
                  <SrText
                    opacity='0.33'
                    font='greycliff'
                    color='yellowGold'
                    weight='800'
                  >
                    N/A
                  </SrText>
                </span>
              </span>
            </div>
          </div>
          <div className='comments'>
            <span className={classnames('comment notReviewed')}>
              No feedback on this listing yet
            </span>
          </div>
        </div>
      )}
      {!tourReviews && (
        <div className='loading-ellipses' />
      )}
    </div>
  );
};

SrPartnerListingTourReviews.propTypes = {
  tourReviews: PropTypes.array,
  currentUser: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrPartnerListingTourReviews.displayName = 'SrPartnerListingTourReviews';

export default SrPartnerListingTourReviews;
